import { useGetAllTalentHuntJobPostsQuery } from "@/app/features/recruitment/job-applicants/job-applicants.api";
import AIIcon from "@/assets/icons/AIIcon";
import CustomTable from "@/components/CustomTable";
import StatusTag from "@/components/StatusTag";
import { DISCLOSER_KEYS, FILTER_KEYS } from "@/constants/keys.constants";
import { useAppDiscloser, useAppFilters } from "@/hooks";
import useAuth from "@/hooks/useAuth";
import { AiSearchIcon, EyeIcon } from "@/icons";
import { Button, Flex, Space, type TableProps } from "antd";
import { useNavigate } from "react-router";
import AiSearchModal from "./_components/TalentHunt/AiSearchModal";
import { formatDate, normalizeColumnValue } from "@/utils";

const TALENT_HUB_STATUS_CONFIG = [
  {
    label: "Processing",
    value: "processing",
    className: "status status-yellow",
  },
  {
    label: "Processing",
    value: "running",
    className: "status status-yellow",
  },
  {
    label: "Completed",
    value: "completed",
    className: "status status-success",
  },
  { label: "N/A", value: "n/a", className: "status status-error" },
];

export default function TalentHunt() {
  const navigate = useNavigate();
  const { organizationId } = useAuth();
  const { page, limit, skip, keyword, filters, handlePagination } =
    useAppFilters(FILTER_KEYS.TALENT_HUNT);

  const [config, handleOpen, handleClose] = useAppDiscloser<"SEARCH">(
    DISCLOSER_KEYS.JOB_APPLICANT,
  );

  const { data: talentHuntData, isFetching } = useGetAllTalentHuntJobPostsQuery(
    {
      params: {
        search: keyword,
        skip,
        limit,
        ...filters,
      },
    },
    { skip: !organizationId },
  );

  const columns: TableProps<any>["columns"] = [
    {
      title: "Job Title",
      dataIndex: "job_title",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Position",
      dataIndex: "designation",
      render: (val: string) => normalizeColumnValue(val, "N/A", false),
    },
    {
      title: "Published On",
      dataIndex: "published_date",
      render: (val) => formatDate(val),
    },
    {
      title: "Suggested Candidates",
      dataIndex: "suggested_candidate",
      render: (val: string) => normalizeColumnValue(val, 0),
    },
    {
      align: "center",
      title: "Ai Search",
      dataIndex: "ai_search_status",
      render: (status: string) => (
        <StatusTag
          value={status || "N/A"}
          shouldCapitalize={false}
          config={TALENT_HUB_STATUS_CONFIG}
        />
      ),
    },
    {
      title: "Actions",
      align: "end",
      render: (record: any) => (
        <Space size={8}>
          <Button
            size="small"
            className="action-btn"
            icon={<EyeIcon size={20} />}
            disabled={
              !record?.ai_search_status ||
              record?.ai_search_status === "processing"
            }
            onClick={() => navigate(`talent-hunt/${record?.job_id}`)}
          />
          <Button
            size="small"
            className="action-btn"
            disabled={record?.ai_search_status === "completed"}
            icon={<AiSearchIcon className="!text-theme-primary" size={20} />}
            onClick={() => handleOpen({ type: "SEARCH", data: record })}
          />
        </Space>
      ),
    },
  ];

  return (
    <>
      <CustomTable
        page={page}
        limit={limit}
        hideRowSelect
        columns={columns}
        tableRootClassName="border-0 p-0"
        dataSource={talentHuntData?.data?.data || []}
        onPaginate={handlePagination}
        customTitle={
          <Flex align="center" gap={10}>
            <span>TalentHunt</span>
            <AIIcon />
          </Flex>
        }
        loading={isFetching}
        total={talentHuntData?.data?.total || 0}
      />

      <AiSearchModal
        open={config.open && config.type === "SEARCH"}
        handleClose={handleClose}
        id={config.data?.job_id}
      />
    </>
  );
}
